const isThemeEditor = Boolean(window.Shopify && window.Shopify.designMode);
let revealObserver = null;

const makeRevealsVisible = (scope = document) => {
  scope.querySelectorAll(".reveal").forEach((el) => el.classList.add("is-visible"));
};

const initRevealElements = (scope = document) => {
  if (isThemeEditor) {
    // In Theme Editor sections re-render often; keep content visible.
    makeRevealsVisible(scope);
    return;
  }

  if (!revealObserver) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            revealObserver?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
  }

  scope.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => revealObserver.observe(el));
};

initRevealElements(document);

if (isThemeEditor) {
  ["shopify:section:load", "shopify:section:select", "shopify:block:select"].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      initRevealElements(event.target || document);
    });
  });
}

const progress = document.querySelector("[data-scroll-progress]");
if (progress) {
  const updateProgress = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const value = max > 0 ? (window.scrollY / max) * 100 : 0;
    progress.style.width = `${Math.min(100, Math.max(0, value))}%`;
  };
  updateProgress();
  window.addEventListener("scroll", updateProgress, { passive: true });
}

/* Announcement bar: slide / rotate mode (section announcement-bar.liquid) */
(() => {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
  if (reduced.matches) return;

  document.querySelectorAll("[data-announcement-slider]").forEach((root) => {
    const slides = [...root.querySelectorAll("[data-announcement-slide]")];
    if (slides.length <= 1) return;

    const pauseOnHover = root.getAttribute("data-pause-hover") === "true";
    const raw = Number.parseInt(root.getAttribute("data-interval") || "5000", 10);
    const ms = Math.max(2000, Number.isNaN(raw) ? 5000 : raw);

    let index = slides.findIndex((el) => el.classList.contains("is-active"));
    if (index < 0) index = 0;

    let timer;

    const go = (next) => {
      slides[index].classList.remove("is-active");
      index = (index + next + slides.length) % slides.length;
      slides[index].classList.add("is-active");
    };

    const tick = () => go(1);

    const start = () => {
      window.clearInterval(timer);
      timer = window.setInterval(tick, ms);
    };

    start();

    if (pauseOnHover) {
      root.addEventListener("mouseenter", () => window.clearInterval(timer));
      root.addEventListener("mouseleave", start);
    }

    const onReduced = () => window.clearInterval(timer);
    if (typeof reduced.addEventListener === "function") reduced.addEventListener("change", onReduced);
    else if (typeof reduced.addListener === "function") reduced.addListener(onReduced);
  });
})();

const menuOverlay = document.querySelector("[data-menu-overlay]");
const menuToggle = document.querySelector("[data-menu-toggle]");
const menuClose = document.querySelector("[data-menu-close]");
if (menuOverlay && menuToggle) {
  const openMenu = () => {
    menuOverlay.classList.add("open");
    menuOverlay.setAttribute("aria-hidden", "false");
    menuToggle.setAttribute("aria-expanded", "true");
    document.body.classList.add("menu-open");
  };
  const closeMenu = () => {
    menuOverlay.classList.remove("open");
    menuOverlay.setAttribute("aria-hidden", "true");
    menuToggle.setAttribute("aria-expanded", "false");
    document.body.classList.remove("menu-open");
  };

  menuToggle.addEventListener("click", openMenu);
  if (menuClose) menuClose.addEventListener("click", closeMenu);
  menuOverlay.addEventListener("click", (event) => {
    if (event.target === menuOverlay) closeMenu();
  });
}

/* Desktop mega menus: click toggle + outside close (hover/focus still works via CSS) */
(() => {
  const mqDesk = window.matchMedia("(min-width: 981px)");

  const closeAllMegas = () => {
    document.querySelectorAll("[data-nav-mega].is-open").forEach((item) => {
      item.classList.remove("is-open");
      item.querySelector("[data-nav-mega-trigger]")?.setAttribute("aria-expanded", "false");
    });
  };

  document.querySelectorAll("[data-nav-mega]").forEach((item) => {
    const trigger = item.querySelector("[data-nav-mega-trigger]");
    if (!trigger) return;
    trigger.addEventListener("click", (e) => {
      if (!mqDesk.matches) return;
      e.preventDefault();
      const wasOpen = item.classList.contains("is-open");
      closeAllMegas();
      if (!wasOpen) {
        item.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
      }
    });
  });

  document.addEventListener("click", (e) => {
    if (!mqDesk.matches) return;
    if (e.target.closest("[data-nav-mega]")) return;
    closeAllMegas();
  });

  const onMq = () => closeAllMegas();
  if (typeof mqDesk.addEventListener === "function") mqDesk.addEventListener("change", onMq);
  else if (typeof mqDesk.addListener === "function") mqDesk.addListener(onMq);
})();

const stickyHeader = document.querySelector("[data-sticky-header]");
if (stickyHeader) {
  let lastY = Math.max(window.scrollY, 0);
  const HIDE_AFTER = 90;
  const HIDE_DELTA = 8;
  const SHOW_DELTA = 1;

  const updateStickyVisibility = () => {
    const currentY = Math.max(window.scrollY, 0);
    const delta = currentY - lastY;
    const menuIsOpen = document.body.classList.contains("menu-open");
    const cartIsOpen =
      document.body.classList.contains("tt-cart-open") ||
      document.body.classList.contains("cart-drawer-open");

    // Always show near top and while overlays are open.
    if (currentY <= HIDE_AFTER || menuIsOpen || cartIsOpen) {
      stickyHeader.classList.remove("header-hidden");
      lastY = currentY;
      return;
    }

    // Hide only on clear downward movement.
    if (delta > HIDE_DELTA) {
      stickyHeader.classList.add("header-hidden");
    // Reveal on even slight upward movement so it does not feel "stuck hidden".
    } else if (delta < -SHOW_DELTA) {
      stickyHeader.classList.remove("header-hidden");
    }

    lastY = currentY;
  };

  updateStickyVisibility();
  window.addEventListener("scroll", updateStickyVisibility, { passive: true });
  window.addEventListener("pageshow", updateStickyVisibility, { passive: true });
}

const counters = document.querySelectorAll("[data-counter]");
if (counters.length) {
  const runCounter = (el) => {
    const target = Number(el.dataset.counter);
    const prefix = el.dataset.prefix || "";
    const suffix = el.dataset.suffix || "";
    const duration = 1200;
    const start = performance.now();
    const step = (time) => {
      const progressRatio = Math.min((time - start) / duration, 1);
      const value = Math.round(target * progressRatio);
      el.textContent = `${prefix}${value}${suffix}`;
      if (progressRatio < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const counterObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          runCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.5 }
  );
  counters.forEach((el) => counterObserver.observe(el));
}

document.querySelectorAll(".acc-btn").forEach((button) => {
  button.addEventListener("click", () => {
    const content = button.nextElementSibling;
    const open = button.getAttribute("aria-expanded") === "true";
    button.setAttribute("aria-expanded", String(!open));
    content.style.maxHeight = open ? "0px" : `${content.scrollHeight}px`;
  });
});

const toast = document.querySelector("[data-toast]");
const showToast = (message) => {
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add("show");
  clearTimeout(showToast.timeoutId);
  showToast.timeoutId = setTimeout(() => toast.classList.remove("show"), 1700);
};

/* Shopify contact forms: merge extra UI fields into contact[body] / contact[phone] before POST */
(() => {
  const mergeStandardContact = (form) => {
    const body = form.querySelector('textarea[name="contact[body]"]');
    if (!body) return;
    const topic = form.querySelector("[data-tt-contact-topic]");
    const order = form.querySelector("[data-tt-contact-order]");
    const parts = [];
    if (topic?.value) parts.push(`Topic: ${topic.value}`);
    if (order?.value?.trim()) parts.push(`Order #: ${order.value.trim()}`);
    if (parts.length) body.value = `${parts.join("\n")}\n\n${body.value.trim()}`;
  };

  const mergeIntlQuote = (form) => {
    const dial = form.querySelector("[data-intl-dial-select]");
    const phoneLocal = form.querySelector("[data-intl-phone-local]");
    if (dial && phoneLocal) {
      const local = phoneLocal.value.trim();
      if (dial.value === "other") phoneLocal.value = local;
      else phoneLocal.value = `${dial.value} ${local}`.trim();
    }
  };

  document.querySelectorAll("form.tt-contact-shopify").forEach((form) => {
    form.addEventListener(
      "submit",
      () => {
        if (form.classList.contains("intl-order-form")) mergeIntlQuote(form);
        else mergeStandardContact(form);
      },
      { capture: true }
    );
  });

  document.querySelectorAll("[data-intl-country]").forEach((country) => {
    const form = country.closest("form");
    const dial = form?.querySelector("[data-intl-dial-select]");
    if (!form || !dial) return;
    country.addEventListener("change", () => {
      const c = country.value;
      if (!c || c === "OTHER") return;
      const opts = [...dial.options];
      const found = opts.find(
        (o) => o.dataset.defaultCountry === c || o.dataset.defaultCountryAlt === c
      );
      if (found && found.value !== "other") dial.value = found.value;
    });
  });
})();

(() => {
  document.querySelectorAll("form.intl-order-form").forEach((form) => {
    const panels = Array.from(form.querySelectorAll("[data-intl-step-panel]"));
    const stepRoot = form.closest(".intl-form-shell") || form;
    const stepItems = Array.from(stepRoot.querySelectorAll("[data-intl-step-item]"));
    const nextBtn = form.querySelector("[data-intl-next]");
    const prevBtn = form.querySelector("[data-intl-prev]");
    const submitBtn = form.querySelector("[data-intl-submit]");
    if (!panels.length || !nextBtn || !prevBtn || !submitBtn) return;

    let step = 1;
    const maxStep = panels.length;

    const setStep = (nextStep) => {
      step = Math.min(maxStep, Math.max(1, nextStep));
      panels.forEach((panel, idx) => {
        const active = idx + 1 === step;
        panel.hidden = !active;
        panel.classList.toggle("is-active", active);
      });
      stepItems.forEach((item, idx) => {
        item.classList.toggle("intl-steps__item--active", idx + 1 === step);
      });
      prevBtn.hidden = step <= 1;
      nextBtn.hidden = step >= maxStep;
      submitBtn.hidden = step !== maxStep;
    };

    const validateStep = (stepIndex) => {
      const panel = panels[stepIndex - 1];
      if (!panel) return true;
      const fields = Array.from(panel.querySelectorAll("input, select, textarea")).filter((el) => !el.disabled);
      for (const field of fields) {
        if (typeof field.reportValidity === "function" && !field.reportValidity()) {
          return false;
        }
      }
      return true;
    };

    nextBtn.addEventListener("click", () => {
      if (!validateStep(step)) return;
      setStep(step + 1);
    });

    prevBtn.addEventListener("click", () => {
      setStep(step - 1);
    });

    form.addEventListener("submit", (event) => {
      if (step !== maxStep) {
        event.preventDefault();
        if (validateStep(step)) setStep(step + 1);
        return;
      }
      validateStep(step);
    });

    setStep(1);
  });
})();

document.querySelectorAll("[data-add-demo]").forEach((button) => {
  button.addEventListener("click", () => {
    showToast("Added to cart demo. Ready for Shopify Ajax cart integration.");
  });
});

const loadFeatureChunk = (src, key) => {
  if (!src || !key) return;
  const flag = `ttChunkLoaded_${key}`;
  if (window[flag]) return;
  window[flag] = true;
  const script = document.createElement("script");
  script.src = src;
  script.defer = true;
  document.body.appendChild(script);
};

const headerSearchSrc = document.body?.dataset?.ttHeaderSearchSrc || "";
if (document.querySelector("[data-search], [data-search-page]")) {
  loadFeatureChunk(headerSearchSrc, "header_search");
}

const cartDrawerSrc = document.body?.dataset?.ttCartDrawerSrc || "";
if (
  document.querySelector("[data-cart-drawer], [data-cart-toggle], [data-ajax-add]") ||
  document.querySelector("form.product-form")
) {
  loadFeatureChunk(cartDrawerSrc, "cart_drawer");
}

(() => {
  const PRODUCT_FEED_CAROUSELS = [
    {
      rootSel: "[data-bestsellers-root]",
      trackSel: "[data-bestsellers-track]",
      carouselClass: "tt-bestsellers__track--carousel",
      slideSel: ".tt-bestsellers__slide",
      controlsSel: "[data-bestsellers-controls]",
      prevSel: "[data-bestsellers-prev]",
      nextSel: "[data-bestsellers-next]",
      dotsWrapSel: "[data-bestsellers-dots]",
      dotAttr: "data-bestsellers-dot",
      dotDatasetKey: "bestsellersDot",
      initKey: "bsCarouselInit",
    },
    {
      rootSel: "[data-new-arrivals-root]",
      trackSel: "[data-new-arrivals-track]",
      carouselClass: "tt-new-arrivals__track--carousel",
      slideSel: ".tt-new-arrivals__slide",
      controlsSel: "[data-new-arrivals-controls]",
      prevSel: "[data-new-arrivals-prev]",
      nextSel: "[data-new-arrivals-next]",
      dotsWrapSel: "[data-new-arrivals-dots]",
      dotAttr: "data-new-arrivals-dot",
      dotDatasetKey: "newArrivalsDot",
      initKey: "naCarouselInit",
    },
  ];

  const bindProductFeedCarousel = (root, cfg) => {
    const track = root.querySelector(cfg.trackSel);
    if (!track || !track.classList.contains(cfg.carouselClass) || track.dataset[cfg.initKey] === "true") return;
    track.dataset[cfg.initKey] = "true";

    const controls = root.querySelector(cfg.controlsSel);
    const prevBtn = controls?.querySelector(cfg.prevSel);
    const nextBtn = controls?.querySelector(cfg.nextSel);
    const dotsWrap = controls?.querySelector(cfg.dotsWrapSel);
    const cards = Array.from(track.querySelectorAll(cfg.slideSel));

    if (cards.length <= 1 || !prevBtn || !nextBtn || !dotsWrap) {
      if (controls) controls.hidden = true;
      return;
    }

    let index = 0;
    let dots = [];
    let maxIndex = 0;
    let ticking = false;

    const getPerView = () => {
      const trackWidth = track.clientWidth || 1;
      const firstCard = cards[0]?.getBoundingClientRect().width || trackWidth;
      return Math.max(1, Math.round(trackWidth / Math.max(1, firstCard)));
    };
    const clampIndex = (value) => Math.max(0, Math.min(maxIndex, value));
    const updateButtons = () => {
      prevBtn.disabled = index <= 0;
      nextBtn.disabled = index >= maxIndex;
    };
    const updateDots = () => {
      dots.forEach((dot, idx) => dot.classList.toggle("is-active", idx === index));
    };
    const scrollToIndex = (nextIndex, smooth = true) => {
      index = clampIndex(nextIndex);
      track.scrollTo({
        left: cards[index]?.offsetLeft || 0,
        behavior: smooth ? "smooth" : "auto",
      });
      updateButtons();
      updateDots();
    };

    const buildDots = () => {
      const perView = getPerView();
      maxIndex = Math.max(0, cards.length - perView);
      dotsWrap.innerHTML = Array.from({ length: maxIndex + 1 })
        .map(
          (_, idx) =>
            `<button class="hero-dot${idx === index ? " is-active" : ""}" type="button" ${cfg.dotAttr}="${idx}" aria-label="Go to products slide ${
              idx + 1
            }"></button>`
        )
        .join("");
      dots = Array.from(dotsWrap.querySelectorAll(`[${cfg.dotAttr}]`));
      dots.forEach((dot) => {
        dot.addEventListener("click", () => {
          scrollToIndex(Number(dot.dataset[cfg.dotDatasetKey]), true);
        });
      });
      updateButtons();
      updateDots();
    };

    const syncByScroll = () => {
      const left = track.scrollLeft;
      let closest = 0;
      let minDiff = Number.POSITIVE_INFINITY;
      for (let i = 0; i <= maxIndex; i += 1) {
        const diff = Math.abs((cards[i]?.offsetLeft || 0) - left);
        if (diff < minDiff) {
          minDiff = diff;
          closest = i;
        }
      }
      if (closest !== index) {
        index = closest;
        updateButtons();
        updateDots();
      }
    };

    prevBtn.addEventListener("click", () => scrollToIndex(index - 1, true));
    nextBtn.addEventListener("click", () => scrollToIndex(index + 1, true));
    track.addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          syncByScroll();
          ticking = false;
        });
      },
      { passive: true }
    );
    window.addEventListener("resize", () => {
      const current = index;
      buildDots();
      scrollToIndex(current, false);
    });

    buildDots();
    scrollToIndex(0, false);
  };

  const initProductFeedCarousels = (scope = document) => {
    if (!scope?.querySelectorAll) return;
    PRODUCT_FEED_CAROUSELS.forEach((cfg) => {
      scope.querySelectorAll(cfg.rootSel).forEach((root) => {
        const track = root.querySelector(cfg.trackSel);
        if (!track) return;
        delete track.dataset[cfg.initKey];
        bindProductFeedCarousel(root, cfg);
      });
    });
  };

  const hydrateProductFeedSection = async (sectionEl) => {
    const sortBy = sectionEl.dataset.ttProductFeedSort;
    const sectionId = sectionEl.dataset.sectionId;
    if (!sortBy || !sectionId || sectionEl.dataset.ttProductFeedHydrated === "true") return;

    const track = sectionEl.querySelector("[data-product-feed-track]");
    if (!track) return;

    sectionEl.setAttribute("aria-busy", "true");

    try {
      const url = `/collections/all?sort_by=${encodeURIComponent(sortBy)}&sections=${encodeURIComponent(sectionId)}`;
      const response = await fetch(url);
      if (!response.ok) return;

      const payload = await response.json();
      const html = payload[sectionId] || payload[Object.keys(payload)[0]];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const nextTrack = doc.querySelector("[data-product-feed-track]");
      if (!nextTrack) return;

      track.innerHTML = nextTrack.innerHTML;
      sectionEl.dataset.ttProductFeedHydrated = "true";
      initProductFeedCarousels(sectionEl);
      initRevealElements(sectionEl);
    } catch (_error) {
      // Keep server-rendered fallback products when hydration fails.
    } finally {
      sectionEl.removeAttribute("aria-busy");
    }
  };

  const initProductFeedHydration = (scope = document) => {
    const sections = scope.matches?.("[data-tt-product-feed]")
      ? [scope, ...scope.querySelectorAll("[data-tt-product-feed]")]
      : [...scope.querySelectorAll("[data-tt-product-feed]")];

    return Promise.all(
      [...new Set(sections)].map((sectionEl) => hydrateProductFeedSection(sectionEl))
    );
  };

  initProductFeedHydration().then(() => {
    initProductFeedCarousels();
  });
  document.addEventListener("shopify:section:load", (event) => {
    const scope = event.target;
    if (scope?.matches?.("[data-tt-product-feed]")) {
      delete scope.dataset.ttProductFeedHydrated;
    }
    scope.querySelectorAll?.("[data-tt-product-feed]").forEach((sectionEl) => {
      delete sectionEl.dataset.ttProductFeedHydrated;
    });
    initProductFeedHydration(scope).then(() => {
      initProductFeedCarousels(scope);
    });
  });
})();

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  const cart = document.querySelector("[data-cart-drawer]");
  if (cart?.classList.contains("cart-drawer--open")) {
    cart.classList.remove("cart-drawer--open");
    cart.setAttribute("aria-hidden", "true");
    document.body.classList.remove("cart-drawer-open");
    const t = document.querySelector("[data-cart-toggle]");
    t?.setAttribute("aria-expanded", "false");
    t?.focus({ preventScroll: true });
    return;
  }
  const openMega = document.querySelector("[data-nav-mega].is-open");
  if (openMega) {
    openMega.classList.remove("is-open");
    openMega.querySelector("[data-nav-mega-trigger]")?.setAttribute("aria-expanded", "false");
    return;
  }
  const filtAside = document.querySelector("[data-collection-filters].is-open");
  if (filtAside) {
    filtAside.classList.remove("is-open");
    const fb = document.querySelector("[data-filter-backdrop]");
    fb?.classList.remove("is-open");
    fb?.setAttribute("hidden", "");
    document.body.classList.remove("filter-drawer-open");
    document.querySelector("[data-filter-drawer-open]")?.setAttribute("aria-expanded", "false");
    filtAside.removeAttribute("aria-modal");
    document.querySelector("[data-filter-drawer-open]")?.focus({ preventScroll: true });
    return;
  }
  const menuOverlay = document.querySelector("[data-menu-overlay]");
  const menuToggle = document.querySelector("[data-menu-toggle]");
  if (menuOverlay?.classList.contains("open")) {
    menuOverlay.classList.remove("open");
    menuOverlay.setAttribute("aria-hidden", "true");
    menuToggle?.setAttribute("aria-expanded", "false");
    document.body.classList.remove("menu-open");
  }
});
